// src/SaleItemsReport.jsx
import React, { useEffect, useMemo, useState } from "react";
import { api } from "./api";

export default function SaleItemsReport({ onBack }) {
  const storeId = Number(localStorage.getItem("store_id") || 0);

  // default: last 7 days
  const today = new Date();
  const fmt = (d) => d.toISOString().slice(0, 10);
  const sevenAgo = new Date(Date.now() - 6 * 24 * 60 * 60 * 1000);

  const [from, setFrom] = useState(fmt(sevenAgo));
  const [to, setTo] = useState(fmt(today));
  const [cashier, setCashier] = useState("");
  const [printed, setPrinted] = useState("all"); // all|yes|no
  const [q, setQ] = useState("");

  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");

  const [cashiers, setCashiers] = useState([]);
  const [rows, setRows] = useState([]);
  const [totals, setTotals] = useState({
    rows_count: 0,
    receipts_count: 0,
    qty_total: 0,
    amount_total: 0,
  });

  const money = (n) => Number(n || 0).toFixed(2);

  const load = async () => {
    if (!storeId) {
      setMsg("Missing store_id in localStorage");
      return;
    }
    try {
      setBusy(true);
      setMsg("");
      const res = await api.get("/server.php/report/sale-items", {
        params: { from, to, cashier, printed, q },
      });
      if (!res.data?.success) {
        setMsg(res.data?.message || "Failed to load report");
        return;
      }
      setCashiers(res.data.cashiers || []);
      setRows(res.data.rows || []);
      setTotals(res.data.totals || totals);
    } catch (e) {
      console.error(e);
      setMsg("Server error loading report");
    } finally {
      setBusy(false);
    }
  };

  const loadSummary = async () => {
    // optional; call only if you add a “summary tab”
    try {
      await api.get("/server.php/report/sale-summary", { params: { from, to } });
    } catch {}
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const exportCSV = () => {
    const header = [
      "id",
      "sale_id",
      "store_id",
      "inventory_id",
      "item_name",
      "qty",
      "unit_price",
      "line_total",
      "cashier",
      "created_at",
      "printed_at",
      "printed_by",
    ];

    const esc = (v) => {
      const s = String(v ?? "");
      if (s.includes('"') || s.includes(",") || s.includes("\n")) return `"${s.replaceAll('"', '""')}"`;
      return s;
    };

    const csv =
      header.join(",") +
      "\n" +
      rows
        .map((r) => header.map((h) => esc(r[h])).join(","))
        .join("\n");

    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `sale_items_${from}_to_${to}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const printReport = () => {
    const html = `
      <div style="font-family:Arial,sans-serif;">
        <div style="display:flex;justify-content:space-between;align-items:flex-end;">
          <div>
            <div style="font-size:18px;font-weight:800;">Sales Items Report</div>
            <div style="color:#555;font-size:12px;margin-top:4px;">
              Store: ${storeId} · From: ${from} · To: ${to}
              ${cashier ? ` · Cashier: ${escapeHtml(cashier)}` : ""}
              · Printed: ${printed}
              ${q ? ` · Search: ${escapeHtml(q)}` : ""}
            </div>
          </div>
          <div style="text-align:right;font-size:12px;color:#555;">
            Rows: ${totals.rows_count}<br/>
            Receipts: ${totals.receipts_count}<br/>
            Qty: ${totals.qty_total}<br/>
            Amount: ${money(totals.amount_total)}
          </div>
        </div>

        <div style="border-top:1px solid #ddd;margin:10px 0;"></div>

        <table style="width:100%;border-collapse:collapse;font-size:12px;">
          <thead>
            <tr>
              ${["Created", "Sale#", "Item", "Qty", "Unit", "Total", "Cashier", "Printed"].map(
                (h) => `<th style="text-align:left;border-bottom:1px solid #ccc;padding:6px 4px;">${h}</th>`
              ).join("")}
            </tr>
          </thead>
          <tbody>
            ${rows
              .map(
                (r) => `
              <tr>
                <td style="border-bottom:1px solid #eee;padding:6px 4px;">${escapeHtml(r.created_at)}</td>
                <td style="border-bottom:1px solid #eee;padding:6px 4px;">${escapeHtml(r.sale_id)}</td>
                <td style="border-bottom:1px solid #eee;padding:6px 4px;">${escapeHtml(r.item_name)}</td>
                <td style="border-bottom:1px solid #eee;padding:6px 4px;text-align:right;">${Number(r.qty)}</td>
                <td style="border-bottom:1px solid #eee;padding:6px 4px;text-align:right;">${money(r.unit_price)}</td>
                <td style="border-bottom:1px solid #eee;padding:6px 4px;text-align:right;">${money(r.line_total)}</td>
                <td style="border-bottom:1px solid #eee;padding:6px 4px;">${escapeHtml(r.cashier)}</td>
                <td style="border-bottom:1px solid #eee;padding:6px 4px;">${r.printed_at ? escapeHtml(r.printed_at) : "NO"}</td>
              </tr>
            `
              )
              .join("")}
          </tbody>
        </table>

        <div style="margin-top:10px;color:#666;font-size:11px;">
          Generated at: ${new Date().toLocaleString()}
        </div>

        <script>window.onload=()=>window.print();</script>
      </div>
    `;

    const w = window.open("", "_blank", "width=980,height=720");
    if (!w) {
      setMsg("Popup blocked. Allow popups to print.");
      return;
    }
    w.document.open();
    w.document.write(`<html><head><title>Sales Items Report</title></head><body>${html}</body></html>`);
    w.document.close();
  };

  const filteredCount = rows.length;

  const byItem = useMemo(() => {
    // client-side grouping for quick “Top Items”
    const map = new Map();
    for (const r of rows) {
      const key = String(r.item_name || "");
      const prev = map.get(key) || { item_name: key, qty_total: 0, amount_total: 0 };
      prev.qty_total += Number(r.qty || 0);
      prev.amount_total += Number(r.line_total || 0);
      map.set(key, prev);
    }
    return Array.from(map.values()).sort((a, b) => b.qty_total - a.qty_total).slice(0, 30);
  }, [rows]);

  const byCashier = useMemo(() => {
    const map = new Map();
    for (const r of rows) {
      const key = String(r.cashier || "");
      const prev = map.get(key) || { cashier: key, qty_total: 0, amount_total: 0, receipts: new Set() };
      prev.qty_total += Number(r.qty || 0);
      prev.amount_total += Number(r.line_total || 0);
      prev.receipts.add(String(r.sale_id));
      map.set(key, prev);
    }
    return Array.from(map.values())
      .map((x) => ({ cashier: x.cashier, qty_total: x.qty_total, amount_total: x.amount_total, receipts: x.receipts.size }))
      .sort((a, b) => b.amount_total - a.amount_total);
  }, [rows]);

  return (
    <div style={styles.page}>
      <div style={styles.topbar}>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <button style={styles.btn} onClick={onBack}>← Back</button>
          <div style={{ fontWeight: 900, fontSize: 16 }}>Sales Items Report</div>
        </div>
        <div style={{ color: "#6b7280", fontSize: 12 }}>Store: {storeId || "-"}</div>
      </div>

      <div style={styles.panel}>
        <div style={styles.filters}>
          <div style={styles.field}>
            <label style={styles.label}>From</label>
            <input style={styles.input} type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
          </div>

          <div style={styles.field}>
            <label style={styles.label}>To</label>
            <input style={styles.input} type="date" value={to} onChange={(e) => setTo(e.target.value)} />
          </div>

          <div style={styles.field}>
            <label style={styles.label}>Cashier</label>
            <select style={styles.input} value={cashier} onChange={(e) => setCashier(e.target.value)}>
              <option value="">All</option>
              {cashiers.map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          </div>

          <div style={styles.field}>
            <label style={styles.label}>Printed</label>
            <select style={styles.input} value={printed} onChange={(e) => setPrinted(e.target.value)}>
              <option value="all">All</option>
              <option value="yes">Printed only</option>
              <option value="no">Not printed</option>
            </select>
          </div>

          <div style={{ ...styles.field, gridColumn: "span 2" }}>
            <label style={styles.label}>Search (item or receipt #)</label>
            <input style={styles.input} value={q} onChange={(e) => setQ(e.target.value)} placeholder="e.g. Tomatoes or 14" />
          </div>

          <div style={{ display: "flex", gap: 8, alignItems: "flex-end" }}>
            <button style={styles.btnPrimary} onClick={load} disabled={busy}>
              {busy ? "Loading..." : "Run"}
            </button>
            <button style={styles.btn} onClick={exportCSV} disabled={busy || rows.length === 0}>Export CSV</button>
            <button style={styles.btn} onClick={printReport} disabled={busy || rows.length === 0}>Print</button>
          </div>
        </div>

        {msg ? <div style={styles.msg}>{msg}</div> : null}

        <div style={styles.kpis}>
          <Kpi label="Rows" value={totals.rows_count} />
          <Kpi label="Receipts" value={totals.receipts_count} />
          <Kpi label="Qty Total" value={totals.qty_total} />
          <Kpi label="Amount Total" value={money(totals.amount_total)} />
          <div style={{ marginLeft: "auto", color: "#6b7280", fontSize: 12 }}>
            Showing: {filteredCount} rows (limit 5000)
          </div>
        </div>
      </div>

      <div style={styles.grid2}>
        <div style={styles.panel}>
          <div style={styles.panelHead}>Details</div>
          <div style={styles.tableWrap}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <Th>Created</Th>
                  <Th>Sale #</Th>
                  <Th>Item</Th>
                  <ThR>Qty</ThR>
                  <ThR>Unit</ThR>
                  <ThR>Total</ThR>
                  <Th>Cashier</Th>
                  <Th>Printed</Th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id}>
                    <Td>{r.created_at}</Td>
                    <Td>{r.sale_id}</Td>
                    <Td>{r.item_name}</Td>
                    <TdR>{Number(r.qty)}</TdR>
                    <TdR>{money(r.unit_price)}</TdR>
                    <TdR>{money(r.line_total)}</TdR>
                    <Td>{r.cashier}</Td>
                    <Td>{r.printed_at ? "YES" : "NO"}</Td>
                  </tr>
                ))}
                {rows.length === 0 && (
                  <tr><Td colSpan={8} style={{ color: "#6b7280", textAlign: "center", padding: 14 }}>No data</Td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div style={styles.panel}>
          <div style={styles.panelHead}>Quick Summary (from loaded rows)</div>

          <div style={{ padding: 12 }}>
            <div style={{ fontWeight: 800, marginBottom: 6 }}>By Cashier</div>
            <div style={styles.smallTableWrap}>
              <table style={styles.table}>
                <thead>
                  <tr>
                    <Th>Cashier</Th>
                    <ThR>Receipts</ThR>
                    <ThR>Qty</ThR>
                    <ThR>Amount</ThR>
                  </tr>
                </thead>
                <tbody>
                  {byCashier.map((x) => (
                    <tr key={x.cashier}>
                      <Td>{x.cashier}</Td>
                      <TdR>{x.receipts}</TdR>
                      <TdR>{x.qty_total}</TdR>
                      <TdR>{money(x.amount_total)}</TdR>
                    </tr>
                  ))}
                  {byCashier.length === 0 && (
                    <tr><Td colSpan={4} style={{ color: "#6b7280", textAlign: "center", padding: 12 }}>No summary</Td></tr>
                  )}
                </tbody>
              </table>
            </div>

            <div style={{ fontWeight: 800, margin: "14px 0 6px" }}>Top Items (Qty)</div>
            <div style={styles.smallTableWrap}>
              <table style={styles.table}>
                <thead>
                  <tr>
                    <Th>Item</Th>
                    <ThR>Qty</ThR>
                    <ThR>Amount</ThR>
                  </tr>
                </thead>
                <tbody>
                  {byItem.map((x) => (
                    <tr key={x.item_name}>
                      <Td>{x.item_name}</Td>
                      <TdR>{x.qty_total}</TdR>
                      <TdR>{money(x.amount_total)}</TdR>
                    </tr>
                  ))}
                  {byItem.length === 0 && (
                    <tr><Td colSpan={3} style={{ color: "#6b7280", textAlign: "center", padding: 12 }}>No top items</Td></tr>
                  )}
                </tbody>
              </table>
            </div>

            <div style={{ marginTop: 12, color: "#6b7280", fontSize: 12 }}>
              Note: Quick Summary is computed from the currently loaded rows (after filters).
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ---------- small components ---------- */
function Kpi({ label, value }) {
  return (
    <div style={styles.kpi}>
      <div style={{ color: "#6b7280", fontSize: 12 }}>{label}</div>
      <div style={{ fontWeight: 900, fontSize: 16 }}>{value}</div>
    </div>
  );
}

function Th({ children }) {
  return <th style={styles.th}>{children}</th>;
}
function ThR({ children }) {
  return <th style={{ ...styles.th, textAlign: "right" }}>{children}</th>;
}
function Td({ children, ...rest }) {
  return <td style={styles.td} {...rest}>{children}</td>;
}
function TdR({ children, ...rest }) {
  return <td style={{ ...styles.td, textAlign: "right" }} {...rest}>{children}</td>;
}

function escapeHtml(s) {
  return String(s ?? "")
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#039;");
}

/* ---------- styles ---------- */
const styles = {
  page: { minHeight: "100vh", background: "#eef1f5", padding: 12, color: "#111827" },
  topbar: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "10px 12px",
    background: "#fff",
    border: "1px solid #cfd6df",
    borderRadius: 12,
    marginBottom: 10,
  },
  panel: { background: "#fff", border: "1px solid #cfd6df", borderRadius: 12, overflow: "hidden" },
  panelHead: { padding: "10px 12px", background: "#eef2f7", borderBottom: "1px solid #e7ecf2", fontWeight: 900 },
  filters: {
    display: "grid",
    gridTemplateColumns: "repeat(6, minmax(140px, 1fr))",
    gap: 10,
    padding: 12,
    borderBottom: "1px solid #e7ecf2",
  },
  field: { display: "flex", flexDirection: "column", gap: 5 },
  label: { fontSize: 12, color: "#6b7280", fontWeight: 700 },
  input: { padding: "10px 10px", border: "1px solid #cfd6df", borderRadius: 10, fontSize: 13, outline: "none" },
  btn: { padding: "10px 12px", border: "1px solid #9aa6b2", borderRadius: 10, background: "#fff", fontWeight: 800, cursor: "pointer" },
  btnPrimary: { padding: "10px 12px", border: "1px solid #1d4ed8", borderRadius: 10, background: "#2563eb", color: "#fff", fontWeight: 900, cursor: "pointer" },
  msg: { padding: "8px 12px", color: "#991b1b", fontWeight: 800, borderBottom: "1px solid #e7ecf2" },
  kpis: { display: "flex", gap: 10, padding: 12, alignItems: "center", flexWrap: "wrap" },
  kpi: { minWidth: 120, padding: 10, border: "1px solid #e7ecf2", borderRadius: 12, background: "#fbfcfe" },
  grid2: { display: "grid", gridTemplateColumns: "1.3fr 1fr", gap: 10, marginTop: 10, alignItems: "start" },
  tableWrap: { overflow: "auto", maxHeight: "68vh" },
  smallTableWrap: { overflow: "auto", maxHeight: 220, border: "1px solid #e7ecf2", borderRadius: 10 },
  table: { width: "100%", borderCollapse: "collapse", fontSize: 13 },
  th: {
    position: "sticky",
    top: 0,
    background: "#eef2f7",
    borderBottom: "1px solid #cfd6df",
    textAlign: "left",
    padding: "8px 10px",
    fontSize: 12,
    color: "#334155",
    textTransform: "uppercase",
    letterSpacing: ".06em",
    zIndex: 1,
  },
  td: { borderBottom: "1px solid #e7ecf2", padding: "8px 10px" },
};